<script>
import Button from 'primevue/button';

export default {
    name: 'PartnerEdit',

    components: { Button },

    props: {
        partner: {
            type: Object,
            required: true,
        },
    },

    emits: ['saved', 'cancel'],

    data() {
        return {
            name: this.partner.name,
            code: this.partner.code,
            source_type: this.partner.source_type ?? '',
            description: this.partner.description ?? '',
            is_active: this.partner.is_active,
            error: null,
            codeTooltipVisible: false,
        };
    },

    computed: {},

    mounted() {},

    methods: {
        async save() {
            this.error = null;
            try {
                await this.$store.dispatch('editPartner', {
                    partnerId: this.partner.id,
                    data: {
                        name: this.name.trim() || undefined,
                        code: this.code.trim() || undefined,
                        source_type: this.source_type || null,
                        description: this.description.trim() || null,
                        is_active: this.is_active,
                    },
                });
                this.$emit('saved');
            } catch (e) {
                this.error = e?.response?.data?.detail ?? 'Failed to update partner.';
            }
        },
    },
};
</script>

<template>
    <div class="partner-edit-form">
        <div class="form-title">Edit: {{ partner.name }}</div>
        <p v-if="error" class="error-text">{{ error }}</p>

        <!-- Row 1: Name + Code on one line -->
        <div class="form-row form-row--inline">
            <label class="form-label form-label--inline">Name</label>
            <input v-model="name" class="input-field name-input" />
            <label class="form-label form-label--inline">
                Code
                <span
                    class="field-tooltip"
                    :class="{ 'field-tooltip--active': codeTooltipVisible }"
                    @click.stop="codeTooltipVisible = !codeTooltipVisible"
                >ⓘ
                    <span v-if="codeTooltipVisible" class="tooltip-bubble">Used in partner link URL: gameartkit.com/?ref={code}</span>
                </span>
            </label>
            <input v-model="code" class="input-field code-input" />
        </div>

        <!-- Row 2: Source type -->
        <div class="form-group">
            <label class="form-label">Source type <span class="optional-label">(optional)</span></label>
            <div class="source-toggle">
                <button
                    type="button"
                    :class="['toggle-btn', { 'toggle-btn--active': source_type === 'influencer' }]"
                    @click="source_type = source_type === 'influencer' ? '' : 'influencer'"
                >Influencer</button>
                <button
                    type="button"
                    :class="['toggle-btn', { 'toggle-btn--active': source_type === 'affiliate' }]"
                    @click="source_type = source_type === 'affiliate' ? '' : 'affiliate'"
                >Affiliate</button>
            </div>
        </div>

        <!-- Row 3: Description -->
        <div class="form-group">
            <label class="form-label">Description <span class="optional-label">(optional)</span></label>
            <textarea v-model="description" class="input-field" rows="2" />
        </div>

        <!-- Row 4: Active toggle + actions -->
        <div class="form-footer">
            <label class="checkbox-label">
                <input type="checkbox" v-model="is_active" />
                <span>Active</span>
            </label>
            <div class="form-actions">
                <Button label="Cancel" severity="secondary" @click="$emit('cancel')" />
                <Button label="Save changes" @click="save" />
            </div>
        </div>
    </div>
</template>

<style scoped>
.partner-edit-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 20px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04);
}
.form-title {
    font-weight: 700;
    font-size: 0.95rem;
    color: #0f172a;
    margin-bottom: 2px;
}
.form-row--inline {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
}
.form-label--inline {
    white-space: nowrap;
    flex-shrink: 0;
    margin-bottom: 0;
}
.name-input {
    flex: 2;
    min-width: 0;
}
.code-input {
    flex: 1;
    min-width: 0;
}
.form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}
.form-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 2px;
}
.field-tooltip {
    position: relative;
    cursor: pointer;
    color: #94a3b8;
    font-size: 0.82rem;
    margin-left: 3px;
    user-select: none;
}
.field-tooltip--active { color: #16a34a; }
.tooltip-bubble {
    position: absolute;
    left: 0;
    top: calc(100% + 4px);
    background: #1e293b;
    color: #f1f5f9;
    font-size: 0.75rem;
    font-weight: normal;
    white-space: nowrap;
    padding: 5px 10px;
    border-radius: 5px;
    z-index: 200;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.18);
}
.form-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 6px;
    border-top: 1px solid #f1f5f9;
    gap: 12px;
}
.checkbox-label {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
    cursor: pointer;
    user-select: none;
}
.form-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}
.optional-label {
    color: #94a3b8;
    font-size: 0.72rem;
    font-weight: 400;
    margin-left: 3px;
}
.source-toggle {
    display: flex;
    gap: 0;
}
.toggle-btn {
    flex: 1;
    padding: 7px 14px;
    font-size: 0.82rem;
    font-weight: 500;
    color: #64748b;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
    line-height: 1.4;
}
.toggle-btn:first-child {
    border-radius: 6px 0 0 6px;
}
.toggle-btn:last-child {
    border-radius: 0 6px 6px 0;
    border-left: none;
}
.toggle-btn:hover:not(.toggle-btn--active) {
    background: #f1f5f9;
    border-color: #cbd5e1;
}
.toggle-btn--active {
    background: #f0fdf4;
    color: #16a34a;
    border-color: #86efac;
    font-weight: 600;
}
.toggle-btn:focus-visible {
    outline: 2px solid #16a34a;
    outline-offset: 1px;
    z-index: 1;
    position: relative;
}
/* Consistent input field styling */
.input-field {
    width: 100%;
    padding: 7px 10px;
    font-size: 0.85rem;
    line-height: 1.4;
    color: #0f172a;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    outline: none;
    box-sizing: border-box;
    font-family: inherit;
    transition: border-color 0.15s;
}
.input-field:focus {
    border-color: #86efac;
    box-shadow: 0 0 0 3px rgba(22,163,74,0.1);
}
.input-field::placeholder {
    color: #94a3b8;
}
textarea.input-field {
    resize: vertical;
    min-height: 58px;
}
</style>
